import {
  Action,
  AnyAction,
  configureStore,
  createListenerMiddleware,
  isRejected,
  ThunkAction,
} from '@reduxjs/toolkit';
import authReducer from '../features/auth/authSlice';
import auditLogsReducer from '../features/auditLogs/auditLogsSlice';
import attributesReducer from '../features/searchBookableAccommodations/attributesSlice';
import bookingsReducer from '../features/bookings/bookingsSlice';
import accommodationsReducer from '../features/accommodations/accommodationsSlice';
import accommodationsSearchReducer from '../features/accommodations/accommodationsSearchSlice';
import accommodationgroupsReducer from '../features/accommodations/accommodationGroupsSlice';
import accommodationTagsReducer from '../features/tags/tagsSlice';
import autocompleteReducer from '../features/autocomplete/autocompleteSlice';
import commissionGroupsReducer from '../features/commissionGroups/commissionGroupSlice';
import commissionGroupReducer from '../features/commissionGroups/commissionGroupDetailsSlice';
import accommodationattributesReducer from '../features/accommodations/accommodationAttributesSlice';
import addonsReducer from '../features/addons/addonSlice';
import identityUsersReducer from '../features/Users/identityUsersSlice';
import countriesReducer from '../features/countries/countriesSlice';
import skiResortsReducer from '../features/skiResorts/skiResortsSlice';
import municipalitiesReducer from '../features/municipalities/municipalitiesSlice';
import feedbacksReducer from '../features/feedbacks/FeedbacksSlice';
import regionsReducer from '../features/regions/regionsSlice';
import placesReducer from '../features/places/placesSlice';
import defaultRatesReducer from '../features/defaultRates/defaultRatesSlice';
import defaultRateWeekdayRulesReducer from '../features/rateRules/defaultRateWeekdayRulesSlice';
import defaultRateRulesReducer from '../features/rateRules/defaultRateRulesSlice';
import seasonRatesReducer from '../features/seasonRates/seasonRatesSlice';
import seasonRateWeekdayRulesReducer from '../features/rateRules/seasonRateWeekdayRulesSlice';
import seasonRateRulesReducer from '../features/rateRules/seasonRateRulesSlice';
import blockedPeriodsReducer from '../features/blockedPeriods/blockedPeriodsSlice';
import blockedPeriodReducer from '../features/blockedPeriods/blockedPeriodDetailsSlice';
import errorsReducer, {
  setApiError,
  setInputErrors,
} from '../features/errors/errorsSlice';
import accountingReducer from '../features/accountings/accountingSlice';
import searchBookableAccommodationsReducer from '../features/searchBookableAccommodations/searchBookableAccommodationsSlice';
import tenantsReducer from '../features/offices/OfficesSlice';
import sitesReducer from '../features/sites/sitesSlice';
import rateToolsReducer from '../features/seasonRateTools/rateToolsSlice';
import statusToastReducer, {
  addStatusToast,
} from '../common/components/StatusToast/StatusToastSlice';
import settlementReportReducer from '../features/statistics/settlementReportSlice';
import dac7Reducer from '../features/dac7/dac7Slice';
import invoicingReducer from '../features/invoicing/invoicesSlice';
import icalReducer from '../features/ical/icalSlice';
// Redux toolkit listener middleware
const listenerMiddleware = createListenerMiddleware();
const submitActionTypes = ['add', 'update', 'modify', 'insert', 'save'];

listenerMiddleware.startListening({
  matcher: isRejected,
  effect: async (action: AnyAction, listenerApi) => {
    if (
      submitActionTypes.some(type => action.type.includes(type)) &&
      action.payload &&
      action.payload.status === 400
    ) {
      listenerApi.dispatch(setInputErrors(action.payload.errors));
      listenerApi.dispatch(
        addStatusToast({
          Title: 'Virhe syötetyissä tiedoissa',
          Message: `Tarkista lomakkeen tiedot: ${Object.entries(
            action.payload.errors,
          )
            .map(([key, value]) => `${key}: ${value}`)
            .join('; ')}`,
          IsFailed: true,
        }),
      );
    } else {
      listenerApi.dispatch(setApiError(action.payload));
    }
  },
});

const reducers = {
  auth: authReducer,
  auditLogs: auditLogsReducer,
  bookings: bookingsReducer,
  accommodations: accommodationsReducer,
  accommodationsSearch: accommodationsSearchReducer,
  accommodationgroups: accommodationgroupsReducer,
  accommodationTags: accommodationTagsReducer,
  commissionGroups: commissionGroupsReducer,
  commissionGroup: commissionGroupReducer,
  accommodationattributes: accommodationattributesReducer,
  autocomplete: autocompleteReducer,
  addons: addonsReducer,
  attributes: attributesReducer,
  identityusers: identityUsersReducer,
  countries: countriesReducer,
  skiResorts: skiResortsReducer,
  municipalities: municipalitiesReducer,
  feedbacks: feedbacksReducer,
  regions: regionsReducer,
  places: placesReducer,
  defaultRates: defaultRatesReducer,
  defaultRateWeekdayRules: defaultRateWeekdayRulesReducer,
  defaultRateRules: defaultRateRulesReducer,
  seasonRates: seasonRatesReducer,
  seasonRateWeekdayRules: seasonRateWeekdayRulesReducer,
  seasonRateRules: seasonRateRulesReducer,
  blockedPeriods: blockedPeriodsReducer,
  blockedPeriod: blockedPeriodReducer,
  errors: errorsReducer,
  accoounting: accountingReducer,
  tenants: tenantsReducer,
  sites: sitesReducer,
  rateTools: rateToolsReducer,
  searchBookableAccommodations: searchBookableAccommodationsReducer,
  statusToast: statusToastReducer,
  reports: settlementReportReducer,
  dac7: dac7Reducer,
  ical: icalReducer,
  invoices: invoicingReducer,
};

export const store = configureStore({
  reducer: reducers,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().prepend(listenerMiddleware.middleware),
  devTools: true,
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
